import { X } from "lucide-react";

const SIZE_CLASSES = {
  md: "max-w-md",
  lg: "max-w-2xl",
};

/**
 * BaseModal — the single modal shell. All modals in the app render inside this.
 *
 * Props:
 * - isOpen: boolean
 * - onClose: function
 * - title: string | ReactNode — rendered in the header
 * - titleIcon: ReactNode — optional icon placed left of the title
 * - children: ReactNode — modal body content
 * - footer: ReactNode — rendered in the footer bar (buttons, actions)
 * - subtitle: string — optional secondary line in the header
 * - size: "md" (default) | "lg"
 * - maxHeight: boolean — clamps height to 80vh for scrollable content (default false)
 * - noPadding: boolean — removes body padding; use when children manage their own layout
 */
export function BaseModal({
  isOpen,
  onClose,
  title,
  titleIcon,
  subtitle,
  children,
  footer,
  size = "md",
  maxHeight = false,
  noPadding = false,
}) {
  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
      />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className={`bg-white rounded-xl shadow-xl w-full ${SIZE_CLASSES[size] ?? SIZE_CLASSES.md} ${maxHeight ? "max-h-[80vh] flex flex-col" : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`flex items-center justify-between p-6 ${footer || children ? "border-b" : ""}`}>
            <div className="flex items-center gap-3">
              {titleIcon && (
                <span className="text-blue-500">{titleIcon}</span>
              )}
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                {subtitle && (
                  <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          {children && (
            <div className={`${noPadding ? "" : "p-6"} ${maxHeight ? "overflow-y-auto flex-1" : ""}`}>
              {children}
            </div>
          )}

          {/* Footer */}
          {footer && (
            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
              {footer}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
